import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';
import advancedBillingService from '../../../services/advancedBillingService';

// ---------------------------------------------------------------------------
// Helper: format amount using the invoice's country currency
// ---------------------------------------------------------------------------
const formatCurrency = (value, currencyCode = 'EUR', language = 'FR') => {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode || 'EUR',
    }).format(value ?? 0);
  } catch {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0);
  }
};

// ---------------------------------------------------------------------------
// Fetch invoices with country currency via join
// ---------------------------------------------------------------------------
const fetchInvoicesWithCurrency = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      id,
      invoice_number,
      client_name,
      status,
      total_amount,
      amount_paid,
      balance_due,
      country_id,
      countries (
        currency_code,
        country_name
      )
    `)
    .not('status', 'eq', 'cancelled')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map((inv) => ({
    id: inv.id,
    invoiceNumber: inv.invoice_number,
    clientName: inv.client_name,
    status: inv.status,
    totalAmount: parseFloat(inv.total_amount || 0),
    amountPaid: parseFloat(inv.amount_paid || 0),
    balanceDue: parseFloat(inv.balance_due || 0),
    countryId: inv.country_id,
    currencyCode: inv.countries?.currency_code || 'EUR',
    countryName: inv.countries?.country_name || '',
  }));
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MilestoneBuilder = ({ language, templates }) => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setInvoicesLoading(true);
        const data = await fetchInvoicesWithCurrency();
        setInvoices(data);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setInvoicesLoading(false);
      }
    };
    load();
  }, []);

  const handleInvoiceSelect = async (invoiceId) => {
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    setSelectedInvoice(invoice || null);
    setPhases([]);

    if (invoice) {
      try {
        const existingPhases = await advancedBillingService?.getPaymentPhases(invoice.id);
        setPhases(existingPhases || []);
      } catch (error) {
        console.error('Error fetching phases:', error);
        setPhases([]);
      }
    }
  };

  const handleApplyTemplate = () => {
    if (!selectedTemplate || !selectedInvoice) return;

    const template = templates?.find((t) => t?.id === selectedTemplate);
    if (!template) return;

    const baseDate = new Date();
    const newPhases = template.phases?.map((phase) => {
      const dueDate = new Date(baseDate);
      dueDate.setDate(dueDate.getDate() + phase.daysOffset);

      return {
        phaseNumber: phase.phaseNumber,
        phaseName: phase.phaseName,
        amount: ((selectedInvoice.totalAmount * phase.percentage) / 100).toFixed(2),
        percentage: phase.percentage,
        dueDate: dueDate.toISOString().split('T')[0],
        status: 'pending',
      };
    });

    setPhases(newPhases);
  };

  const handleAddPhase = () => {
    setPhases([
      ...phases,
      {
        phaseNumber: phases.length + 1,
        phaseName: '',
        amount: 0,
        dueDate: new Date().toISOString().split('T')[0],
        status: 'pending',
      },
    ]);
  };

  const handlePhaseChange = (index, field, value) => {
    const newPhases = [...phases];
    newPhases[index] = { ...newPhases[index], [field]: value };
    setPhases(newPhases);
  };

  const handleRemovePhase = (index) => {
    const renumbered = phases
      .filter((_, i) => i !== index)
      .map((phase, i) => ({ ...phase, phaseNumber: i + 1 }));
    setPhases(renumbered);
  };

  const handleSavePhases = async () => {
    if (!selectedInvoice || phases.length === 0) return;

    setLoading(true);
    try {
      // Remove existing phases
      const existingPhases = await advancedBillingService?.getPaymentPhases(selectedInvoice.id);
      for (const phase of existingPhases || []) {
        await advancedBillingService?.deletePaymentPhase(phase.id);
      }

      // Insert new phases
      for (const phase of phases) {
        await advancedBillingService?.createPaymentPhase({
          invoiceId: selectedInvoice.id,
          phaseNumber: phase.phaseNumber,
          phaseName: phase.phaseName,
          amount: parseFloat(phase.amount),
          dueDate: phase.dueDate,
          status: phase.status || 'pending',
        });
      }

      alert('Phases enregistrées avec succès');
    } catch (error) {
      console.error('Error saving phases:', error);
      alert("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const currency = selectedInvoice?.currencyCode || 'EUR';
  const totalPhaseAmount = phases.reduce(
    (sum, phase) => sum + parseFloat(phase.amount || 0),
    0
  );
  const totalMatchesInvoice =
    selectedInvoice &&
    Math.abs(totalPhaseAmount - selectedInvoice.totalAmount) < 0.01;

  const invoiceOptions = invoicesLoading
    ? [{ value: '', label: 'Chargement…' }]
    : invoices.map((inv) => ({
        value: inv.id,
        label: `${inv.invoiceNumber} - ${inv.clientName} (${formatCurrency(
          inv.totalAmount,
          inv.currencyCode,
          language
        )})`,
      }));

  const templateOptions = [
    {
      value: '',
      label: 'Sélectionner un template',
    },
    ...(templates || []).map((t) => ({ value: t.id, label: t.name })),
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Invoice selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Sélection de la facture
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Facture"
            options={invoiceOptions}
            value={selectedInvoice?.id || ''}
            onChange={handleInvoiceSelect}
            disabled={invoicesLoading}
          />

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select
                label="Appliquer un template"
                options={templateOptions}
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                disabled={!selectedInvoice}
              />
            </div>
            <Button
              onClick={handleApplyTemplate}
              disabled={!selectedTemplate || !selectedInvoice}
              variant="outline"
            >
              Appliquer
            </Button>
          </div>
        </div>

        {/* Invoice summary using resolved currency */}
        {selectedInvoice && (
          <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">
                  Client :
                </span>
                <p className="font-medium text-foreground">
                  {selectedInvoice.clientName}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">
                  Montant total :
                </span>
                <p className="font-semibold text-foreground">
                  {formatCurrency(selectedInvoice.totalAmount, currency, language)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">
                  Statut :
                </span>
                <p className="font-medium text-foreground capitalize">
                  {selectedInvoice.status}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">
                  Devise :
                </span>
                <p className="font-medium text-foreground">
                  {currency}
                  {selectedInvoice.countryName
                    ? ` — ${selectedInvoice.countryName}`
                    : ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Phase builder */}
      {selectedInvoice && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Phases de paiement
            </h3>
            <Button onClick={handleAddPhase} variant="outline" size="sm">
              <Icon name="Plus" size={16} className="mr-2" />
              Ajouter phase
            </Button>
          </div>

          <div className="space-y-4">
            {phases.map((phase, index) => (
              <div
                key={index}
                className="bg-muted/30 border border-border rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                      {phase.phaseNumber}
                    </div>
                    <span className="font-medium text-foreground">
                      Phase {phase.phaseNumber}
                    </span>
                  </div>
                  {phases.length > 1 && (
                    <button
                      onClick={() => handleRemovePhase(index)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    label="Nom de la phase"
                    value={phase.phaseName}
                    onChange={(e) =>
                      handlePhaseChange(index, 'phaseName', e.target.value)
                    }
                  />
                  {/* Label shows the actual currency symbol */}
                  <Input
                    label={`Montant (${currency})`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={phase.amount}
                    onChange={(e) =>
                      handlePhaseChange(index, 'amount', e.target.value)
                    }
                  />
                  <Input
                    label="Date d'échéance"
                    type="date"
                    value={phase.dueDate}
                    onChange={(e) =>
                      handlePhaseChange(index, 'dueDate', e.target.value)
                    }
                  />
                </div>

                {/* Per-phase formatted preview */}
                {parseFloat(phase.amount) > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground text-right">
                    {formatCurrency(parseFloat(phase.amount), currency, language)}
                  </p>
                )}
              </div>
            ))}
          </div>

          {phases.length > 0 && (
            <div className="mt-6 space-y-4">
              {/* Total vs invoice amount validation */}
              <div
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  totalMatchesInvoice
                    ? 'bg-success/10 border-success/30'
                    : 'bg-warning/10 border-warning/30'
                }`}
              >
                <div>
                  <span className="font-medium text-foreground">
                    Total des phases
                  </span>
                  {!totalMatchesInvoice && (
                    <p className="text-xs text-warning mt-0.5">
                      Différence : {formatCurrency(
                        Math.abs(
                          totalPhaseAmount - selectedInvoice.totalAmount
                        ),
                        currency,
                        language
                      )}
                    </p>
                  )}
                </div>
                <span
                  className={`text-xl font-bold ${
                    totalMatchesInvoice ? 'text-success' : 'text-warning'
                  }`}
                >
                  {formatCurrency(totalPhaseAmount, currency, language)}
                </span>
              </div>

              <Button
                onClick={handleSavePhases}
                fullWidth
                loading={loading}
              >
                <Icon name="Save" size={18} className="mr-2" />
                Enregistrer les echéances de paiement
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MilestoneBuilder;